/**
 * ClassificationBanner — response classification banner above the message
 * list (R1 P1 #5).
 *
 * Driven by the canonical SSE `metadata.classification` field
 * (CLAUDE.md §20.7 + §11.3 "Responses inherit highest source
 * classification on both axes"). Only renders when the response
 * inherits something stricter than the platform default
 * (`unclassified` / `none`).
 *
 * Visual tiers (founder spec, conservative — never display anything
 * remotely close to a SCIF banner; this is a chat UI hint):
 *   - CUI            → blue
 *   - Restricted     → red
 *   - ITAR           → amber + "US Person only" suffix
 *   - EAR            → amber
 *
 * Pure presentational. Logic lives in the route's metadata reducer.
 */

import { cn } from '@/lib/utils';

interface ClassificationBannerProps {
  /**
   * `{sensitivity, exportControl}` as emitted by the backend in the SSE
   * `metadata.classification` field. Pass undefined to hide the banner.
   */
  classification?: {
    sensitivity: string;
    exportControl: string;
  };
  className?: string;
}

export const SENSITIVITY_LABEL: Record<string, string> = {
  unclassified: 'Unclassified',
  cui: 'CUI',
  restricted: 'Restricted',
};
export const EXPORT_LABEL: Record<string, string> = {
  none: '',
  itar: 'ITAR (US Person only)',
  ear: 'EAR',
};

/**
 * Pure-logic decision: should the classification banner render for the
 * given backend classification payload? Returns null when the response
 * is at the platform default on both axes (no banner), or an object
 * describing the visible label parts + chosen color tone.
 *
 * Exposed for unit testing — the React component delegates to this.
 */
export function deriveClassificationBanner(
  classification: { sensitivity?: string; exportControl?: string } | undefined,
): { parts: string[]; tone: 'amber' | 'red' | 'blue' } | null {
  if (!classification) return null;
  const sensitivity = classification.sensitivity?.toLowerCase();
  const exportControl = classification.exportControl?.toLowerCase();

  if (
    (sensitivity === 'unclassified' || !sensitivity) &&
    (exportControl === 'none' || !exportControl)
  ) {
    return null;
  }

  const parts: string[] = [];
  if (sensitivity && SENSITIVITY_LABEL[sensitivity]) {
    parts.push(SENSITIVITY_LABEL[sensitivity]);
  }
  if (exportControl && EXPORT_LABEL[exportControl]) {
    parts.push(EXPORT_LABEL[exportControl]);
  }

  const isItar = exportControl === 'itar';
  const isRestricted = sensitivity === 'restricted';
  const tone: 'amber' | 'red' | 'blue' = isItar ? 'amber' : isRestricted ? 'red' : 'blue';
  return { parts, tone };
}

const TONE_CLASSES: Record<'amber' | 'red' | 'blue', string> = {
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  red: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40',
  blue: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
};

export function ClassificationBanner({
  classification,
  className,
}: ClassificationBannerProps) {
  const decision = deriveClassificationBanner(classification);
  if (!decision) return null;

  return (
    <div
      data-testid="ask-argus-classification-banner"
      data-sensitivity={classification?.sensitivity?.toLowerCase()}
      data-export-control={classification?.exportControl?.toLowerCase()}
      className={cn(
        'flex items-center gap-2 px-4 py-1.5 border-b text-[11px] font-medium shrink-0',
        TONE_CLASSES[decision.tone],
        className,
      )}
    >
      <span>Response classification:</span>
      <span className="font-semibold">{decision.parts.join(' · ')}</span>
    </div>
  );
}
